"use client";

import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  IconButton,
  Typography,
  InputAdornment,
  Box,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import { MdVerified } from "react-icons/md";
import {
  Close as CloseIcon,
  Instagram,
  YouTube,
  Facebook,
  Twitter,
  LinkedIn,
  Reddit,
} from "@mui/icons-material";
import { SiTwitch, SiSnapchat, SiQuora } from "react-icons/si";
import axios from "axios";
import axiosMain from "../http/axiosMain";

// Box wrapper for sections
const BoxWithBorder = ({ title, children }) => (
  <Box border="1px solid #ccc" borderRadius={2} p={2} mt={1}>
    <Typography variant="subtitle1" fontWeight="medium" mb={2}>
      {title}
    </Typography>
    {children}
  </Box>
);

const AddInfluencerModal = ({ open, handleClose }) => {
  const socialLinksMaster = [
    "instagram",
    "youtube",
    "facebook",
    "twitter",
    "twitch",
    "snapchat",
    "quora",
    "reddit",
    "linkedin",
  ];

  const SocialMediaDetails = {
    instagram: {
      key: "instagram",
      placeholder: "Instagram Handle",
      icon: (props) => <Instagram sx={{ color: "#E4405F" }} {...props} />,
    },
    youtube: {
      key: "youtube",
      placeholder: "YouTube Handle",
      icon: (props) => <YouTube sx={{ color: "#FF0000" }} {...props} />,
    },
    facebook: {
      key: "facebook",
      placeholder: "Facebook Handle",
      icon: (props) => <Facebook sx={{ color: "#1877F2" }} {...props} />,
    },
    twitter: {
      key: "twitter",
      placeholder: "Twitter Handle",
      icon: (props) => <Twitter sx={{ color: "#1DA1F2" }} {...props} />,
    },
    twitch: {
      key: "twitch",
      placeholder: "Twitch Handle",
      icon: (props) => <SiTwitch color="#9146FF" size={24} {...props} />,
    },
    snapchat: {
      key: "snapchat",
      placeholder: "Snapchat Handle",
      icon: (props) => <SiSnapchat color="#FFFC00" size={24} {...props} />,
    },
    quora: {
      key: "quora",
      placeholder: "Quora Handle",
      icon: (props) => <SiQuora color="#B92B27" size={24} {...props} />,
    },
    reddit: {
      key: "reddit",
      placeholder: "Reddit Handle",
      icon: (props) => <Reddit sx={{ color: "#FF4500" }} {...props} />,
    },
    linkedin: {
      key: "linkedin",
      placeholder: "LinkedIn Handle",
      icon: (props) => <LinkedIn sx={{ color: "#0A66C2" }} {...props} />,
    },
  };

  // State
  const [socialLinks, setSocialLinks] = useState(
    Object.fromEntries(socialLinksMaster.map((key) => [key, ""]))
  );
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);

  // Snackbar
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const handleInputChange = (platform, value) => {
    setSocialLinks((prev) => ({
      ...prev,
      [platform]: value,
    }));
  };

  const handleSubmit = async () => {
    const isAllEmpty =
      Object.values(socialLinks).every((val) => !val.trim()) && !note.trim();

    if (isAllEmpty) {
      setSnackbar({
        open: true,
        message: "Please fill at least one social link or note.",
        severity: "warning",
      });
      return;
    }

    const payload = {
      social_media_platform: socialLinks,
      note: note,
    };

    console.log("Submitting Payload:", payload);

    try {
      setLoading(true);
      const response = await axiosMain.post(
        "/api/brand/request-influencer/",
        payload
      );
      console.log("API Response:", response.data);

      setSnackbar({
        open: true,
        message: "Influencer request sent successfully!",
        severity: "success",
      });

      // Auto-close modal after success
      setTimeout(() => {
        handleClose();
        resetForm();
      }, 1500);
    } catch (error) {
      console.error("API Error:", error);
      setSnackbar({
        open: true,
        message: "Failed to send request. Please try again.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setSocialLinks(
      Object.fromEntries(socialLinksMaster.map((key) => [key, ""]))
    );
    setNote("");
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            p: 1,
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box>
              <Typography variant="h6" fontWeight="bold">
                Request Influencer
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.5 }}
              >
                Add influencer whom you want to see on this platform
              </Typography>
            </Box>
            <IconButton
              onClick={handleClose}
              size="small"
              sx={{ color: "text.secondary" }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ pt: 2 }}>
          <Grid container spacing={3}>
            {/* Social Media Handles */}
            <Grid item xs={12}>
              <BoxWithBorder title="Social Media Handles">
                <Grid container spacing={2}>
                  {socialLinksMaster.map((link) => {
                    const { icon, key, placeholder } = SocialMediaDetails[link];
                    return (
                      <Grid item xs={12} md={6} lg={4} key={key}>
                        <TextField
                          size="small"
                          fullWidth
                          placeholder={placeholder}
                          value={socialLinks[link]}
                          onChange={(e) =>
                            handleInputChange(link, e.target.value)
                          }
                          sx={{
                            "& .MuiInputBase-root": { height: "48px" },
                          }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                {icon({ size: "24px" })}
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                    );
                  })}
                </Grid>
              </BoxWithBorder>
            </Grid>

            {/* Note */}
            {/* <Grid item xs={12}>
              <TextField
                size="small"
                fullWidth
                placeholder="Enter a note for the influencer request..."
                multiline
                rows={3}
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            </Grid> */}
          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, pt: 2 }}>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading}
            sx={{
              backgroundColor: "#6B8E76 !important",
              // "&:hover": { backgroundColor: "#45a049" },
              borderRadius: 1,
              textTransform: "none",
              px: 3,
              boxShadow: "none",
              color: "#ffffff"
            }}
          >
            {loading ? (
              <CircularProgress size={20} sx={{ color: "#fff" }} />
            ) : (
              "Request Influencer"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar Notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default AddInfluencerModal;
